import type { RouterOutputs } from './core/trpc'
import type { ParcelCard } from './parcel-card'

/**
 * The operator-facing words for a correction the server refused (ADR-0032,
 * ADR-0036). The server decides who may cancel a handover or a cash payment, and
 * when — the operator within their Shift, support after it — and says why not;
 * the screen only names the reason. The same reasons come with the parcel detail
 * (`corrections`) and with a refused act.
 */

type Corrections = ParcelCard['corrections']
export type CashCancelReason = NonNullable<Corrections['cancelCash']['reason']>
export type HandoverCancelReason = NonNullable<Corrections['cancelHandover']['reason']>

const CASH: Record<CashCancelReason, string> = {
	handed_over: 'payment.cancelHandedOver',
	not_on_shelf: 'payment.cancelNotOnShelf',
	cashbox_short: 'payment.cancelCashboxShort',
	outside_your_shift: 'payment.cancelOutsideShift',
	shift_still_open: 'payment.cancelShiftStillOpen',
	not_permitted: 'payment.cancelNotPermitted',
	nothing_to_cancel: 'payment.cancelNothing',
	wrong_payment_source: 'payment.cancelWrongSource',
}

const HANDOVER: Record<HandoverCancelReason, string> = {
	not_handed_over: 'handoverCancel.notHandedOver',
	outside_your_shift: 'handoverCancel.outsideShift',
	shift_still_open: 'handoverCancel.shiftStillOpen',
	not_permitted: 'handoverCancel.notPermitted',
}

/** The key naming why a cash payment cannot be cancelled. */
export function cashCancelReasonKey(reason: CashCancelReason): string {
	return CASH[reason]
}

/** The key naming why a handover cannot be cancelled. */
export function handoverCancelReasonKey(reason: HandoverCancelReason): string {
	return HANDOVER[reason]
}

type CancelCashResult = RouterOutputs['payments']['CancelCollected']

/** The key naming why a cash cancellation did not go through, or null when it did. */
export function cashCancelResultKey(result: CancelCashResult): string | null {
	switch (result.outcome) {
		case 'cancelled':
		case 'already_cancelled':
			return null
		case 'wrong_payment_source':
			return CASH.wrong_payment_source
		case 'refused':
			return CASH[result.reason]
	}
}
